// utils/Axios.js

import axios from 'axios';
import {
  buildLoginUrl,
  getToken,
  getRefreshToken,
  removeToken,
  saveToken,
  scheduleTokenRefresh,
} from './Auth';
import store from '../store';
import { API_URI } from 'Constant';
import { detectBrowserAndDevice } from 'Common/Functions';

const Axios = axios.create();

// Add token to every request
Axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-DeviceId'] = detectBrowserAndDevice().deviceId;
    return config;
  },
  (error) => Promise.reject(error),
);

// Handle logout
const logoutHandler = async () => {
  // Remember the page the session expired on so login can send the user back.
  const loginUrl = buildLoginUrl();
  try {
    const detectionResult = detectBrowserAndDevice();
    await axios.post(`${API_URI}/clients/logout`, {
      deviceInfo: {
        deviceId: detectionResult.deviceId,
        deviceName: `${detectionResult.device} ${detectionResult.browser}`,
      },
    });
  } catch (err) {
    console.error('Logout API failed:', err);
  } finally {
    removeToken();
    store.dispatch({ type: 'USER_LOGOUT' });
    window.location.href = `${window.location.origin}${loginUrl}`;
  }
};

// Token refresh logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshAccessToken = async () => {
  try {
    const res = await axios.post(`${API_URI}/api/auth/refreshtokenapp`, {
      refreshToken: getRefreshToken(),
    });
    if (res.status === 200) {
      const { accessToken, refreshTokenDuration } = res.data;
      saveToken(accessToken, refreshTokenDuration);
      scheduleTokenRefresh(refreshAccessToken);
      return accessToken;
    } else {
      await logoutHandler();
      throw new Error('Unable to refresh token');
    }
  } catch (err) {
    await logoutHandler();
    throw err;
  }
};

// Response interceptor
Axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { config, response } = err;
    if (!response) {
      console.error('No response from server:', err);
      return Promise.reject(err);
    }

    const { status } = response;

    if (status === 401 || status === 406) {
      await logoutHandler();
      return Promise.reject(err);
    }
    // If 417 - Refresh Token
    if (status === 417 && !config._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              config.headers.Authorization = 'Bearer ' + token;
              config._retry = true;
              resolve(Axios(config));
            },
            reject: (err) => reject(err),
          });
        });
      }

      config._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        config.headers.Authorization = 'Bearer ' + newToken;
        return Axios(config); // retry original request
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  },
);

// Helpers
export const handelError = (Err) => {
  let errMsg = 'Something Went Wrong';
  if (Err?.message) errMsg = Err.message;
  return {
    error: true,
    message: errMsg,
  };
};

export const header = {
  headers: {
    'Content-Type': 'application/json',
  },
};

export default Axios;
